import React, { useState, useEffect } from 'react';
import { FaCheck, FaChartLine, FaCogs, FaDollarSign, FaLaptop, FaBullhorn, FaUsers } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import StartSidebar from '../components/StartSidebar';

const Solutions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const isStartContext = location.pathname.startsWith('/start/');
  const [availableItems, setAvailableItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMeetingTypes, setSelectedMeetingTypes] = useState(new Set());
  const [selectedDepartments, setSelectedDepartments] = useState(new Set());
  const [weeklyHoursMin, setWeeklyHoursMin] = useState('');
  const [weeklyHoursMax, setWeeklyHoursMax] = useState('');

  // Load data from Firestore on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load available items
        const itemsSnapshot = await getDocs(collection(db, 'availableItems'));
        const itemsData = [];
        itemsSnapshot.forEach(doc => {
          itemsData.push({ id: doc.id, ...doc.data() });
        });
        setAvailableItems(itemsData);


      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);




  // Get items for a specific department
  const getItemsForDepartment = (department) => {
    return availableItems.filter(item => item.department === department);
  };

  // Handle meeting type selection
  const toggleMeetingType = (meetingType) => {
    setSelectedMeetingTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(meetingType)) {
        newSet.delete(meetingType);
      } else {
        newSet.add(meetingType);
      }
      return newSet;
    });
  };

  // Handle department selection
  const toggleDepartment = (department) => {
    setSelectedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(department)) {
        newSet.delete(department);
      } else {
        newSet.add(department);
      }
      return newSet;
    });
  };

  // Check if continue button should be enabled
  const isContinueEnabled = () => {
    return selectedDepartments.size > 0 && 
           selectedMeetingTypes.size > 0 && 
           weeklyHoursMin && 
           weeklyHoursMax;
  };

  // Handle continue button click
  const handleContinue = async () => {
    if (!isContinueEnabled()) return;

    if (currentUser) {
      try {
        // Prepare solutions data
        const solutionsData = {
          selectedDepartments: Array.from(selectedDepartments),
          selectedMeetingTypes: Array.from(selectedMeetingTypes),
          weeklyHoursMin: parseInt(weeklyHoursMin),
          weeklyHoursMax: parseInt(weeklyHoursMax),
          submittedAt: new Date().toISOString()
        };

        // Get user document first to access existing data
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        const completedSteps = userData.startFlowProgress?.completedSteps || [];
        
        // Ensure step 3 is added if not already present (Solutions is step 3)
        const updatedCompletedSteps = completedSteps.includes(3) 
          ? completedSteps 
          : [...completedSteps, 3];
        
        // Save solutions data to user document
        await updateDoc(doc(db, 'users', currentUser.uid), {
          solutionsData: solutionsData,
          startFlowProgress: {
            completedSteps: updatedCompletedSteps,
            lastUpdated: new Date().toISOString()
          },
          lastUpdated: new Date().toISOString()
        });

        // Find and update the associated lead account
        const accountsSnapshot = await getMyAccountSnapshot('Leads');
        
        if (!accountsSnapshot.empty) {
          const leadAccount = accountsSnapshot.docs[0];
          const leadData = leadAccount.data();
          const currentDate = new Date().toISOString();
          const updatedData = {
            solutionsData: solutionsData,
            lastUpdated: currentDate
          };
          
          // Always update substatus to "Solutions Chosen"
          // "Solutions Chosen" belongs to the Assessment stage
          const currentStage = leadData.stage || 'Lead Received';
          const stageOrder = ['Lead Received', 'Discovery Call', 'Assessment', 'Proposal', 'Onboarding Call'];
          const currentStageIndex = stageOrder.indexOf(currentStage);
          const assessmentStageIndex = stageOrder.indexOf('Assessment');
          
          // Only update stage to Assessment if we're not already in a later stage
          if (currentStageIndex < assessmentStageIndex) {
            updatedData.stage = 'Assessment';
            console.log(`Updating stage from "${currentStage}" to "Assessment"`);
          }
          
          // Always update substatus to "Solutions Chosen"
          updatedData.substatus = 'Solutions Chosen';
          console.log('Updating substatus to "Solutions Chosen"');
          
          // Track substatus dates
          const substatusDates = leadData.substatusDates || {};
          if (!substatusDates['Solutions Chosen']) {
            substatusDates['Solutions Chosen'] = currentDate;
          }
          updatedData.substatusDates = substatusDates;
          
          // Add to status history
          const statusHistory = leadData.statusHistory || [];
          const statusEntry = {
            status: updatedData.stage || currentStage,
            substatus: 'Solutions Chosen',
            previousStatus: currentStage,
            previousSubstatus: leadData.substatus || null,
            date: currentDate,
            updatedBy: 'system',
            action: 'Solutions chosen - substatus updated automatically'
          };
          updatedData.statusHistory = [...statusHistory, statusEntry];
          
          await updateDoc(doc(db, 'accounts', leadAccount.id), updatedData);
          console.log('Solutions data saved to lead account:', leadAccount.id);
          console.log('Updated data:', updatedData);
        } else {
          console.log('No lead account found for user:', currentUser.email);
        }
        
        // Navigate back to dashboard
        navigate('/start/dashboard');
      } catch (error) {
        console.error('Error completing step:', error);
      }
    }
  };

  // Get department color
  const getDepartmentColor = (department) => {
    const colors = {
      'Sales': 'bg-red-500',
      'Operations': 'bg-blue-500',
      'Finance': 'bg-green-500',
      'Technology': 'bg-purple-500',
      'Marketing': 'bg-orange-500',
      'HR': 'bg-yellow-500',
      'General': 'bg-gray-500'
    };
    return colors[department] || colors['General'];
  };

  // Get department icon
  const getDepartmentIcon = (department) => {
    const icons = {
      'Sales': FaChartLine,
      'Operations': FaCogs,
      'Finance': FaDollarSign,
      'Technology': FaLaptop,
      'Marketing': FaBullhorn,
      'HR': FaUsers,
      'General': FaCogs
    };
    return icons[department] || icons['General'];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <StartSidebar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Row */}
        <div className="flex items-center justify-center mb-8">
          {/* Page Title */}
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            Choose Your Solutions
          </h1>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading solutions...
            </div>
          </div>
        )}

        {/* Content - Only show when not loading */}
        {!isLoading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Department Cards */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                Departments
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'].map(department => {
                  const items = getItemsForDepartment(department);
                  const IconComponent = getDepartmentIcon(department);
                  const isSelected = selectedDepartments.has(department);
                  
                  return (
                    <div 
                      key={department} 
                      onClick={() => toggleDepartment(department)}
                      className={`rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                        isSelected
                          ? `${getDepartmentColor(department)} ring-2 ring-${getDepartmentColor(department).split('-')[1]}-500`
                          : `${getDepartmentColor(department).replace('-500', '-100')} dark:${getDepartmentColor(department).replace('-500', '-900/20')} hover:${getDepartmentColor(department).replace('-500', '-200')} dark:hover:${getDepartmentColor(department).replace('-500', '-800/30')}`
                      }`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                          isSelected ? 'bg-white/20' : 'bg-white/60 dark:bg-white/20'
                        }`}>
                          <IconComponent className={`text-2xl ${
                            isSelected ? 'text-white' : `text-${getDepartmentColor(department).split('-')[1]}-600 dark:text-${getDepartmentColor(department).split('-')[1]}-400`
                          }`} />
                        </div>
                        
                        <h3 className={`text-xl font-semibold mb-2 ${
                          isSelected ? 'text-white' : `text-${getDepartmentColor(department).split('-')[1]}-800 dark:text-${getDepartmentColor(department).split('-')[1]}-200`
                        }`}>
                          {department}
                        </h3>
                        
                        {isSelected && (
                          <div className="flex justify-center">
                            <FaCheck className="text-2xl text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Continue Button */}
              <div className="mt-6 w-full">
                <button
                  onClick={handleContinue}
                  disabled={!isContinueEnabled()}
                  className={`w-full px-6 py-6 rounded-lg font-medium text-2xl transition-colors ${
                    isContinueEnabled()
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  Continue
                </button>
              </div>
            </div>

            {/* Right Side - Time Investment and Meeting Time */}
            <div className="space-y-6">
              {/* Time Investment Cards */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                  Time Investment
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Weekly Hours
                      </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 text-center">
                      I'm willing to invest this many hours into growing my business a week
                    </p>
                    <div className="flex items-center justify-center space-x-4">
                      <div className="flex flex-col items-center">
                        <input
                          type="number"
                          min="1"
                          max="168"
                          placeholder="0"
                          value={weeklyHoursMin}
                          onChange={(e) => setWeeklyHoursMin(e.target.value)}
                          className="w-20 px-3 py-2 text-2xl font-bold text-blue-600 dark:text-blue-400 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                        />
                        <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">Min</span>
                      </div>
                      <span className="text-lg text-gray-400 dark:text-gray-500">-</span>
                      <div className="flex flex-col items-center">
                        <input
                          type="number"
                          min="1"
                          max="168"
                          placeholder="0"
                          value={weeklyHoursMax}
                          onChange={(e) => setWeeklyHoursMax(e.target.value)}
                          className="w-20 px-3 py-2 text-2xl font-bold text-blue-600 dark:text-blue-400 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                        />
                        <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">Max</span>
                      </div>
                      <span className="text-lg text-gray-600 dark:text-gray-400">hours/week</span>
                    </div>
                  </div>

                </div>
              </div>

              {/* Meeting Time Cards */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                  Involvement
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    onClick={() => toggleMeetingType('community-calls')}
                    className={`rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      selectedMeetingTypes.has('community-calls')
                        ? 'bg-green-500 ring-2 ring-green-500'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        selectedMeetingTypes.has('community-calls') ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        Community Calls
                      </h3>
                      <p className={`text-sm mb-4 ${
                        selectedMeetingTypes.has('community-calls') ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        Peer Discussions
                      </p>
                      {selectedMeetingTypes.has('community-calls') && (
                        <div className="flex justify-center">
                          <FaCheck className="text-2xl text-white" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div 
                    onClick={() => toggleMeetingType('group-classes')}
                    className={`rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      selectedMeetingTypes.has('group-classes')
                        ? 'bg-green-500 ring-2 ring-green-500'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        selectedMeetingTypes.has('group-classes') ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        Group Classes
                      </h3>
                      <p className={`text-sm mb-4 ${
                        selectedMeetingTypes.has('group-classes') ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        Expert Led Teachings
                      </p>
                      {selectedMeetingTypes.has('group-classes') && (
                        <div className="flex justify-center">
                          <FaCheck className="text-2xl text-white" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div 
                    onClick={() => toggleMeetingType('one-on-one')}
                    className={`rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      selectedMeetingTypes.has('one-on-one')
                        ? 'bg-green-500 ring-2 ring-green-500'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        selectedMeetingTypes.has('one-on-one') ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        1 on 1 Coaching
                      </h3>
                      <p className={`text-sm mb-4 ${
                        selectedMeetingTypes.has('one-on-one') ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        Individual Calls
                      </p>
                      {selectedMeetingTypes.has('one-on-one') && (
                        <div className="flex justify-center">
                          <FaCheck className="text-2xl text-white" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div 
                    onClick={() => toggleMeetingType('in-person')}
                    className={`rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      selectedMeetingTypes.has('in-person')
                        ? 'bg-green-500 ring-2 ring-green-500'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        selectedMeetingTypes.has('in-person') ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        In Person Visit
                      </h3>
                      <p className={`text-sm mb-4 ${
                        selectedMeetingTypes.has('in-person') ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        BBA On-Site Visit
                      </p>
                      {selectedMeetingTypes.has('in-person') && (
                        <div className="flex justify-center">
                          <FaCheck className="text-2xl text-white" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div 
                    onClick={() => toggleMeetingType('courses')}
                    className={`rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      selectedMeetingTypes.has('courses')
                        ? 'bg-green-500 ring-2 ring-green-500'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        selectedMeetingTypes.has('courses') ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        Courses
                      </h3>
                      <p className={`text-sm mb-4 ${
                        selectedMeetingTypes.has('courses') ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        Full Course Library Access
                      </p>
                      {selectedMeetingTypes.has('courses') && (
                        <div className="flex justify-center">
                          <FaCheck className="text-2xl text-white" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div 
                    onClick={() => toggleMeetingType('workshops')}
                    className={`rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      selectedMeetingTypes.has('workshops')
                        ? 'bg-green-500 ring-2 ring-green-500'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        selectedMeetingTypes.has('workshops') ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        Workshops
                      </h3>
                      <p className={`text-sm mb-4 ${
                        selectedMeetingTypes.has('workshops') ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        Business Enhancement Pages
                      </p>
                      {selectedMeetingTypes.has('workshops') && (
                        <div className="flex justify-center">
                          <FaCheck className="text-2xl text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Solutions;
